// components/ChoreGroup.tsx
import React from 'react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { Urgency } from '../domain';
import ChoreRow from './ChoreRow';
import { Card, StatusDot, Tone, cx, focusRing } from './ui';

const GROUP_TONE: Record<Urgency, Tone> = {
  overdue: 'overdue',
  today: 'today',
  thisWeek: 'upcoming',
  later: 'neutral',
};

interface ChoreGroupProps {
  urgency: Urgency;
  label: string;
  chores: { id: string; household_id: string }[];
  /** Makes the heading a toggle that folds the group away, as Later is by default. */
  collapsible?: boolean;
  open?: boolean;
  onToggle?: () => void;
}

/** One urgency group on the dashboard: a heading with its count, and the chores in a card. */
export const ChoreGroup: React.FC<ChoreGroupProps> = ({ urgency, label, chores, collapsible, open = true, onToggle }) => {
  const listId = `chore-group-${urgency}`;
  const heading = (
    <>
      <StatusDot tone={GROUP_TONE[urgency]} />
      <span>{label}</span>
      <span className="font-medium text-gray-400 tabular-nums dark:text-gray-400">{chores.length}</span>
    </>
  );

  return (
    <section aria-labelledby={`${listId}-heading`} className="space-y-2">
      <h3 id={`${listId}-heading`} className="px-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
        {collapsible ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-controls={listId}
            className={cx('flex items-center gap-2 rounded-md hover:text-indigo-700 dark:hover:text-indigo-300', focusRing)}
          >
            {heading}
            <ChevronDownIcon
              className={cx('h-4 w-4 text-gray-400 dark:text-gray-500 transition-transform motion-reduce:transition-none', !open && '-rotate-90')}
              aria-hidden="true"
            />
          </button>
        ) : (
          <span className="flex items-center gap-2">{heading}</span>
        )}
      </h3>
      {open && (
        <Card id={listId} padding="none" className="divide-y divide-gray-100 dark:divide-gray-800 overflow-hidden">
          {chores.map(chore => (
            <ChoreRow key={chore.id} householdId={chore.household_id} choreId={chore.id} />
          ))}
        </Card>
      )}
    </section>
  );
};

export default ChoreGroup;
